/**
 * AminoBae — Workout Tracker UI
 * Initialises every <section class="wt"> on the page. Multi-section safe
 * (Theme Editor previews can duplicate the section). Re-init is suppressed
 * via root.__wtInited so re-renders don't double-bind listeners.
 *
 * The customer-namespace globals (window.WorkoutDataNamespace,
 * window.WorkoutCustomer) are emitted inline in the .liquid file because
 * they need the {{ customer }} object. workout-data.js reads them on load.
 */
(function () {
  function ready(fn) {
    if (window.WorkoutData) return fn();
    setTimeout(() => ready(fn), 30);
  }
  ready(() => {
    document.querySelectorAll('section.wt').forEach((root) => {
      if (root.__wtInited) return;
      root.__wtInited = true;
      initWorkoutTracker(root);
    });
    // Theme Editor support: re-init when sections are reloaded.
    document.addEventListener('shopify:section:load', (e) => {
      e.target.querySelectorAll('section.wt').forEach((root) => {
        if (root.__wtInited) return;
        root.__wtInited = true;
        initWorkoutTracker(root);
      });
    });
  });

  function initWorkoutTracker(root) {
    const WD = window.WorkoutData;
    const $  = (s, c = root) => c.querySelector(s);
    const $$ = (s, c = root) => Array.from(c.querySelectorAll(s));

    // ── State ────────────────────────────────────────
    const state = {
      session: { date: todayISO(), exercises: [], unit: WD.getSettings().units === 'imperial' ? 'lb' : 'kg' },
    };

    function isCardioExercise(exOrName) {
      if (WD && typeof WD.isCardioExercise === 'function') {
        return WD.isCardioExercise(exOrName);
      }
      if (!exOrName) return false;
      if (typeof exOrName === 'object') {
        if (String(exOrName.muscle || '').toLowerCase() === 'cardio') return true;
        return isCardioExercise(exOrName.name);
      }
      const name = String(exOrName).toLowerCase().trim();
      const CARDIO_NAMES = [
        'treadmill run', 'treadmill walk (incline)', 'treadmill walk', 'treadmill',
        'stationary bike', 'exercise bike', 'spin bike', 'cycling', 'bike',
        'rowing machine', 'rower',
        'stair master', 'stair climber', 'stairs',
        'elliptical', 'cross trainer',
        'jump rope', 'skipping',
        'battle ropes',
        'sled push', 'sled pull',
        'burpee', 'burpees',
        'box jump', 'box jumps',
        'assault bike', 'air bike', 'echo bike',
        'ski erg', 'running', 'jogging', 'walking', 'sprints'
      ];
      return CARDIO_NAMES.some((c) => name === c || name.includes(c));
    }

    function todayISO() {
      const d = new Date();
      const off = d.getTimezoneOffset();
      const local = new Date(d.getTime() - off * 60000);
      return local.toISOString().slice(0, 10);
    }
    function escapeHTML(s) {
      return String(s || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[c]);
    }
    function formatDate(iso) {
      if (!iso) return '';
      if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const [y, m, d] = iso.split('-').map(Number);
        const localDate = new Date(y, m - 1, d);
        return localDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      }
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }

    // ── Tabs ─────────────────────────────────────────
    // Wired with the WAI-ARIA tab pattern: click to activate, plus
    // ArrowLeft/ArrowRight/Home/End for keyboard nav. Only the active tab
    // is in the tab order (tabindex=0); inactive tabs are tabindex=-1 so
    // a keyboard user lands on the *active* tab when tabbing into the
    // tablist, then arrow-keys between them. (Audit fix AX2.)
    const tabEls = $$('.wt__tab');
    function activateTab(t) {
      const id = t.dataset.tab;
      tabEls.forEach((b) => {
        const active = b === t;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.setAttribute('tabindex', active ? '0' : '-1');
      });
      $$('.wt__panel').forEach((p) => {
        p.hidden = p.dataset.panel !== id;
        p.classList.toggle('is-active', p.dataset.panel === id);
      });
      if (id === 'history')  renderHistory();
      if (id === 'weight')   renderBodyWeight();
      if (id === 'routines') renderRoutines();
      // Hide the sticky finish bar on non-log tabs (it's only relevant
      // when actively logging a workout). Re-evaluate on log tab.
      const stickyBarLocal = root.querySelector('[data-wt-sticky]');
      if (stickyBarLocal) {
        if (id === 'log') {
          if (typeof updateStickyBar === 'function') updateStickyBar();
        } else {
          stickyBarLocal.classList.remove('is-visible');
          stickyBarLocal.setAttribute('aria-hidden', 'true');
        }
      }
    }
    // Initial roving-tabindex state
    tabEls.forEach((t) => t.setAttribute('tabindex', t.classList.contains('is-active') ? '0' : '-1'));
    tabEls.forEach((t, idx) => {
      t.addEventListener('click', () => activateTab(t));
      t.addEventListener('keydown', (e) => {
        let next = null;
        if (e.key === 'ArrowRight') next = tabEls[(idx + 1) % tabEls.length];
        else if (e.key === 'ArrowLeft') next = tabEls[(idx - 1 + tabEls.length) % tabEls.length];
        else if (e.key === 'Home')      next = tabEls[0];
        else if (e.key === 'End')       next = tabEls[tabEls.length - 1];
        if (!next) return;
        e.preventDefault();
        activateTab(next);
        next.focus();
      });
    });

    // ── Custom selects (.wt-cs) ────────────────────────
    // Replaces native <select>s with a styled dropdown that matches the
    // dark theme. Each .wt-cs has a hidden <input> for value tracking
    // (existing JS reads .value and listens for 'change' on it).
    // Tracks all custom-select instances so we can close any open one from
    // a single document-level outside-click handler instead of registering
    // N listeners (one per [data-cs]) and leaking them on Theme Editor
    // re-renders. (Audit fix C1.)
    const csCloseFns = [];
    function initCustomSelects() {
      $$('[data-cs]').forEach((cs) => {
        const btn       = cs.querySelector('.wt-cs__btn');
        const labelEl   = cs.querySelector('[data-cs-label]');
        const inputEl   = cs.querySelector('[data-cs-input]');
        const panel     = cs.querySelector('.wt-cs__panel');
        const options   = Array.from(cs.querySelectorAll('[data-cs-option]'));
        if (!btn || !panel || !inputEl) return;

        const open = () => {
          panel.hidden = false;
          cs.dataset.csOpen = 'true';
          btn.setAttribute('aria-expanded', 'true');
          // Focus the currently-selected option for keyboard nav
          const sel = options.find((o) => o.getAttribute('aria-selected') === 'true');
          (sel || options[0])?.focus();
        };
        const close = () => {
          panel.hidden = true;
          cs.dataset.csOpen = 'false';
          btn.setAttribute('aria-expanded', 'false');
        };
        const toggle = () => (cs.dataset.csOpen === 'true' ? close() : open());
        csCloseFns.push({ cs, close });

        const select = (opt) => {
          if (!opt) return;
          const value = opt.dataset.csOption;
          const text  = opt.textContent.trim();
          inputEl.value = value;
          if (labelEl) labelEl.textContent = text;
          options.forEach((o) => o.setAttribute('aria-selected', o === opt ? 'true' : 'false'));
          inputEl.dispatchEvent(new Event('change', { bubbles: true }));
          close();
          btn.focus();
        };

        btn.addEventListener('click', toggle);
        options.forEach((opt) => {
          opt.addEventListener('click', () => select(opt));
          opt.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(opt); }
            if (e.key === 'ArrowDown') { e.preventDefault(); (options[options.indexOf(opt) + 1] || options[0]).focus(); }
            if (e.key === 'ArrowUp')   { e.preventDefault(); (options[options.indexOf(opt) - 1] || options[options.length - 1]).focus(); }
            if (e.key === 'Escape')    { close(); btn.focus(); }
          });
        });
        // Keyboard open from the trigger button
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') close();
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        });
      });
    }
    initCustomSelects();

    // Single document-level outside-click closes whichever select is open.
    // Replaces the per-instance handlers that previously leaked.
    document.addEventListener('click', (e) => {
      csCloseFns.forEach(({ cs, close }) => {
        if (cs.dataset.csOpen === 'true' && !cs.contains(e.target)) close();
      });
    });

    // Programmatically update a custom-select's display state without
    // firing 'change' (used during init when restoring saved values).
    function setCustomSelect(inputEl, value) {
      if (!inputEl) return;
      const cs = inputEl.closest('[data-cs]');
      if (!cs) { inputEl.value = value; return; }
      const opt = cs.querySelector('[data-cs-option="' + CSS.escape(String(value)) + '"]');
      if (!opt) { inputEl.value = value; return; }
      inputEl.value = value;
      const labelEl = cs.querySelector('[data-cs-label]');
      if (labelEl) labelEl.textContent = opt.textContent.trim();
      cs.querySelectorAll('[data-cs-option]').forEach((o) => {
        o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
      });
    }

    // ── Themed confirm modal ─────────────────────────
    // Promise-based replacement for window.confirm(). Uses the same dark-
    // theme dialog shell as wt-rdialog so destructive prompts (delete
    // session, replace workout) don't render in jarring native chrome.
    // Returns Promise<boolean>. (Audit fix C8.)
    // ── Focus trap utility ────────────────────────────────────────────
    // Constrains keyboard Tab/Shift+Tab within a modal container so
    // keyboard users cannot reach obscured page content behind the backdrop.
    // Returns a cleanup function that removes the listener on close.
    function trapFocus(container) {
      const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      function getFocusable() { return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => !el.closest('[hidden]')); }
      function onKeyDown(e) {
        if (e.key !== 'Tab') return;
        const els = getFocusable();
        if (!els.length) { e.preventDefault(); return; }
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
      container.addEventListener('keydown', onKeyDown);
      return () => container.removeEventListener('keydown', onKeyDown);
    }

    let scrollLockCount = 0;
    let savedScrollY = 0;
    function lockScroll() {
      if (scrollLockCount === 0) {
        savedScrollY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.classList.add('wt-modal-open');
      }
      scrollLockCount++;
    }
    function unlockScroll() {
      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.classList.remove('wt-modal-open');
        window.scrollTo(0, savedScrollY);
      }
    }

    function wtConfirm(message, opts) {
      opts = opts || {};
      return new Promise((resolve) => {
        const opener = document.activeElement;
        const overlay = document.createElement('div');
        overlay.className = 'wt-rdialog';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.innerHTML = ''
          + '<div class="wt-rdialog__backdrop" data-wt-cf-close></div>'
          + '<div class="wt-rdialog__panel" style="max-width:400px;">'
          +   '<header class="wt-rdialog__head">'
          +     '<h3 class="wt-rdialog__title">' + escapeHTML(opts.title || 'Confirm') + '</h3>'
          +   '</header>'
          +   '<p class="wt-rdialog__sub" style="margin:0 0 14px;">' + escapeHTML(message) + '</p>'
          +   '<div class="wt-rdialog__actions">'
          +     '<button type="button" class="wt__btn wt__btn--ghost" data-wt-cf-cancel>' + escapeHTML(opts.cancel || 'Cancel') + '</button>'
          +     '<button type="button" class="wt__btn ' + (opts.danger ? 'wt__btn--danger' : 'wt__btn--primary') + '" data-wt-cf-ok>' + escapeHTML(opts.ok || 'Confirm') + '</button>'
          +   '</div>'
          + '</div>';
        document.body.appendChild(overlay);
        lockScroll();
        // Activate focus trap within the confirm overlay
        const releaseTrap = trapFocus(overlay.querySelector('.wt-rdialog__panel'));
        const cleanup = (result) => {
          releaseTrap();
          document.removeEventListener('keydown', onKey);
          unlockScroll();
          overlay.remove();
          if (opener && typeof opener.focus === 'function') { try { opener.focus(); } catch (_) {} }
          resolve(result);
        };
        function onKey(e) {
          if (e.key === 'Escape') { e.preventDefault(); cleanup(false); }
        }
        document.addEventListener('keydown', onKey);
        overlay.querySelector('[data-wt-cf-ok]').addEventListener('click', () => cleanup(true));
        overlay.querySelectorAll('[data-wt-cf-cancel], [data-wt-cf-close]').forEach(
          (el) => el.addEventListener('click', () => cleanup(false))
        );
        // Default focus on the safer (Cancel) option for destructive prompts with preventScroll
        setTimeout(() => {
          const cancelBtn = overlay.querySelector('[data-wt-cf-cancel]');
          if (cancelBtn) cancelBtn.focus({ preventScroll: true });
        }, 50);
      });
    }

    // ── Toast helper (brief feedback for tap actions) ────
    let toastEl = null;
    let toastTimer = null;
    function showToast(msg, customDuration) {
      if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.className = 'wt-toast';
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastEl);
      }
      toastEl.textContent = msg;
      toastEl.classList.add('is-visible');
      clearTimeout(toastTimer);
      const duration = customDuration || Math.max(2200, Math.min(4500, (String(msg || '').length) * 60));
      toastTimer = setTimeout(() => {
        if (toastEl) toastEl.classList.remove('is-visible');
      }, duration);
    }

    // ── Draft persistence ────────────────────────────────
    // Save the in-progress session under a separate localStorage key so a
    // refresh / accidental nav doesn't lose half-logged work. Auto-restored
    // on init UNLESS stale (>12h old — prevents the sticky bar from
    // permanently reappearing across days due to a forgotten session).
    const DRAFT_KEY = 'aminobae_workout_draft_v1';
    const DRAFT_MAX_AGE = 12 * 60 * 60 * 1000;
    function persistDraft() {
      try {
        const payload = Object.assign({}, state.session, { _savedAt: Date.now() });
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
      } catch (_) {}
    }
    function loadDraft() {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.exercises) || !parsed.exercises.length) return null;
        const age = Date.now() - (parsed._savedAt || 0);
        if (age > DRAFT_MAX_AGE) { clearDraft(); return null; }
        return parsed;
      } catch (_) {}
      return null;
    }
    function clearDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch (_) {} }

    // ── Rest timer ───────────────────────────────────
    // Configurable: enabled flag + duration are persisted to localStorage so
    // the user's preference sticks across visits. Driven by a select in the
    // session-meta row (Off / 30s / 60s / 90s / 2m / 3m / 4m).
    const TIMER_PREF_KEY = 'aminobae_timer_pref_v1';
    function loadTimerPref() {
      try {
        const raw = localStorage.getItem(TIMER_PREF_KEY);
        if (raw) {
          const p = JSON.parse(raw);
          if (p && Number.isFinite(p.duration)) return { duration: p.duration };
        }
      } catch (_) {}
      return { duration: 90 }; // default 90s
    }
    function saveTimerPref(pref) {
      try { localStorage.setItem(TIMER_PREF_KEY, JSON.stringify(pref)); } catch (_) {}
    }
    let timerPref = loadTimerPref();
    const timerEl     = $('[data-wt-timer]');
    const timerValue  = $('[data-timer-value]');
    const timerLabel  = $('[data-timer-label]');
    let timerEnd      = 0;     // epoch ms when current rest ends
    let timerInterval = null;
    // Guard against Theme Editor re-init leaking the timer interval
    if (root.__wtTimerInterval) { clearInterval(root.__wtTimerInterval); root.__wtTimerInterval = null; }
    function pad2(n) { return n < 10 ? '0' + n : '' + n; }
    function fmtTime(s) {
      s = Math.max(0, Math.round(s));
      const m = Math.floor(s / 60); const sec = s % 60;
      return m + ':' + pad2(sec);
    }
    // Compact rest-time format: <60s shows "45s", >=60s shows "1:30"
    function fmtRestShort(s) {
      s = Math.max(0, Math.round(s));
      const m = Math.floor(s / 60); const sec = s % 60;
      return m > 0 ? m + ':' + pad2(sec) : sec + 's';
    }
    // Set-type cycle: tap the set number to rotate through these states
    const SET_TYPES = ['normal', 'warmup', 'drop', 'failure'];
    const SET_TYPE_LABELS = { normal: 'Normal set', warmup: 'Warmup set', drop: 'Drop set', failure: 'Set to failure' };
    const SET_TYPE_TAGS   = { warmup: 'W', drop: 'D', failure: 'F' };
    function tickTimer() {
      const remaining = (timerEnd - Date.now()) / 1000;
      if (remaining <= 0) { finishTimer(); return; }
      if (timerValue) timerValue.textContent = fmtTime(remaining);
    }
    function startRestTimer(seconds) {
      if (!timerEl) return;
      // duration of 0 = timer disabled — silently no-op
      const dur = seconds || timerPref.duration || 0;
      if (dur <= 0) return;
      timerEnd = Date.now() + dur * 1000;
      timerEl.classList.remove('is-finished');
      if (timerLabel) timerLabel.textContent = 'Rest';
      if (timerValue) timerValue.textContent = fmtTime(dur);
      timerEl.classList.add('is-visible');
      clearInterval(timerInterval);
      root.__wtTimerInterval = timerInterval = setInterval(tickTimer, 250);
    }
    function adjustTimer(delta) {
      if (!timerEnd) return;
      timerEnd += delta * 1000;
      // If we just dropped below zero by adjusting down, finish immediately
      if (timerEnd <= Date.now()) finishTimer();
      else tickTimer();
    }
    function playTimerChime() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        if (ctx.state === 'suspended') {
          ctx.resume();
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        const now = ctx.currentTime;
        osc.frequency.setValueAtTime(660, now);
        osc.frequency.exponentialRampToValueAtTime(880, now + 0.14);
        gain.gain.setValueAtTime(0.28, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.60);
        setTimeout(() => {
          try { ctx.close(); } catch (_) {}
        }, 750);
      } catch (_) {}
    }
    function finishTimer() {
      clearInterval(timerInterval);
      timerInterval = null;
      if (timerLabel) timerLabel.textContent = 'Rest done';
      if (timerValue) timerValue.textContent = '0:00';
      if (timerEl) {
        timerEl.classList.add('is-finished');
        playTimerChime();
        if (typeof navigator.vibrate === 'function') navigator.vibrate([180, 90, 180]);
      }
      // Auto-hide after a brief celebratory window
      setTimeout(skipTimer, 2400);
    }
    function skipTimer() {
      clearInterval(timerInterval);
      timerInterval = null;
      timerEnd = 0;
      if (timerEl) {
        timerEl.classList.remove('is-visible');
        timerEl.classList.remove('is-finished');
      }
    }
    // Wire timer controls
    $$('[data-timer-adjust]', timerEl).forEach((b) => {
      b.addEventListener('click', () => adjustTimer(parseInt(b.dataset.timerAdjust, 10) || 0));
    });
    $('[data-timer-skip]', timerEl)?.addEventListener('click', skipTimer);

    // ── Session meta ─────────────────────────────────
    const dateInput  = $('#wt-session-date');
    const unitInput  = $('#wt-session-units');
    const restInput  = $('#wt-session-rest');
    // Date pill label — the readable formatted text shown in the styled pill.
    // The actual <input type="date"> is hidden; the pill click opens it.
    const datePillLabel = $('[data-wt-date-label]');
    function updateDatePill() {
      if (!datePillLabel || !dateInput) return;
      datePillLabel.textContent = formatDate(dateInput.value || todayISO());
    }
    if (dateInput) {
      dateInput.value = state.session.date;
      dateInput.addEventListener('change', () => {
        state.session.date = dateInput.value || todayISO();
        updateDatePill();
      });
    }
    updateDatePill();
    // For the custom-select hidden inputs, also sync the visible label/aria.
    setCustomSelect(unitInput, state.session.unit);
    if (restInput) setCustomSelect(restInput, String(timerPref.duration));
    unitInput.addEventListener('change', () => {
      state.session.unit = unitInput.value;
      // Rerender exercises so unit suffix updates everywhere
      renderExercises();
    });
    if (restInput) {
      restInput.addEventListener('change', () => {
        const v = parseInt(restInput.value, 10);
        timerPref.duration = Number.isFinite(v) ? v : 0;
        saveTimerPref(timerPref);
        // If the user just disabled the timer mid-rest, hide it
        if (timerPref.duration === 0) skipTimer();
      });
    }

    // ── Render exercises in active session ───────────
    const exercisesEl    = $('[data-wt-exercises]');
    const emptyEl        = $('[data-wt-exercises-empty]');
    const finishBtn      = $('[data-wt-finish]');
    const saveRoutineBtn = $('[data-wt-save-routine]');

    function renderExercises() {
      // Clear all but the empty placeholder
      $$('.wt-ex', exercisesEl).forEach((n) => n.remove());

      if (!state.session.exercises.length) {
        emptyEl.hidden = false;
        finishBtn.disabled = true;
        if (saveRoutineBtn) saveRoutineBtn.disabled = true;
        return;
      }
      emptyEl.hidden = true;
      if (saveRoutineBtn) saveRoutineBtn.disabled = false;

      const totalEx = state.session.exercises.length;
      state.session.exercises.forEach((ex, exIdx) => {
        const isCardio = isCardioExercise(ex);
        const distUnit = state.session.unit === 'lb' ? 'mi' : 'km';
        const card = document.createElement('div');
        card.className = 'wt-ex' + (isCardio ? ' wt-ex--cardio' : '');
        card.dataset.exIdx = exIdx;
        const collapsed = (typeof ex._collapsed === 'boolean')
          ? ex._collapsed
          : (exIdx !== totalEx - 1);
        if (collapsed) card.classList.add('is-collapsed');
        ex._collapsed = collapsed;

        const last = WD.getLastPerformance(ex.name);
        let best = null;
        let lastFormatted = '—';
        let bestFormatted = '—';
        let suggestFormatted = 'Set baseline';
        let suggest = null;

        if (isCardio) {
          if (last) {
            const t = last.time != null ? last.time : (last.reps || 0);
            const d = last.distance != null ? last.distance : (last.weight || 0);
            if (t > 0 && d > 0) lastFormatted = `${t} min · ${d} ${distUnit}`;
            else if (t > 0)     lastFormatted = `${t} min`;
            else if (d > 0)     lastFormatted = `${d} ${distUnit}`;
          }

          // Best for cardio: highest distance (or time) in history
          WD.getSessions().forEach((s) => {
            s.exercises.forEach((e) => {
              if (e.name !== ex.name) return;
              e.sets.forEach((set) => {
                if (!set.completed) return;
                const t = set.time != null ? set.time : (set.reps || 0);
                const d = set.distance != null ? set.distance : (set.weight || 0);
                if (t <= 0 && d <= 0) return;
                const score = (d * 1000) + t;
                if (!best || score > best.score) {
                  best = { time: t, distance: d, score };
                }
              });
            });
          });
          if (best) {
            if (best.distance > 0 && best.time > 0) bestFormatted = `${best.time} min · ${best.distance} ${distUnit}`;
            else if (best.time > 0)                 bestFormatted = `${best.time} min`;
            else if (best.distance > 0)             bestFormatted = `${best.distance} ${distUnit}`;
          }

          // Suggested for cardio
          if (last) {
            const t = last.time != null ? last.time : (last.reps || 0);
            const d = last.distance != null ? last.distance : (last.weight || 0);
            if (t > 0 || d > 0) {
              const nextTime = t > 0 ? (t >= 30 ? t + 5 : t + 2) : 0;
              const nextDist = d > 0 ? +(d * 1.05).toFixed(2) : 0;
              suggest = { time: nextTime, distance: nextDist };
              if (nextTime > 0 && nextDist > 0) suggestFormatted = `${nextTime} min · ${nextDist} ${distUnit}`;
              else if (nextTime > 0)             suggestFormatted = `${nextTime} min`;
              else if (nextDist > 0)             suggestFormatted = `${nextDist} ${distUnit}`;
            }
          }
        } else {
          // Standard strength
          if (last && (last.weight || last.reps)) {
            lastFormatted = (last.weight || 0) + ' × ' + (last.reps || 0);
          }
          WD.getSessions().forEach((s) => {
            s.exercises.forEach((e) => {
              if (e.name !== ex.name) return;
              e.sets.forEach((set) => {
                if (!set.completed || !set.weight || !set.reps) return;
                const oneRm = WD.estimated1RM(set.weight, set.reps);
                if (!best || oneRm > best.oneRm) best = { weight: set.weight, reps: set.reps, unit: set.unit || s.unit || 'kg', oneRm };
              });
            });
          });
          if (best) bestFormatted = best.weight + ' × ' + best.reps;

          const wStep = state.session.unit === 'lb' ? 5 : 2.5;
          if (last && last.weight) {
            const reps = last.reps || 0;
            if (reps >= 8) suggest = { weight: +(last.weight + wStep).toFixed(2), reps: 5 };
            else           suggest = { weight: last.weight, reps: reps + 1 };
            suggestFormatted = suggest.weight + ' × ' + suggest.reps;
          }
        }

        // INTEL row — Last · Best · Suggested (premium progression panel)
        const intelHTML = `
          <div class="wt-ex__intel" aria-label="Progression intelligence">
            <div class="wt-ex__intel-item">
              <span class="wt-ex__intel-label">Last</span>
              <span class="wt-ex__intel-value">${lastFormatted}</span>
            </div>
            <div class="wt-ex__intel-item">
              <span class="wt-ex__intel-label">Best</span>
              <span class="wt-ex__intel-value">${bestFormatted}</span>
            </div>
            <div class="wt-ex__intel-item wt-ex__intel-item--suggest">
              <span class="wt-ex__intel-label">Suggested</span>
              <span class="wt-ex__intel-value">${suggestFormatted}</span>
            </div>
          </div>
        `;

        // "Use last" quick-fill — only if there's a previous performance
        const useLastHTML = (last && lastFormatted !== '—')
          ? `<button type="button" class="wt-ex__use-last" data-action="use-last">
               <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8v4l3 2M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 4 21 9 16 9"/></svg>
               Use last (${lastFormatted})
             </button>`
          : '';

        // Mini sparkline of last 5 entries
        const sparkPoints = WD.exerciseProgression(ex.name).slice(-5);
        const sparkHTML = sparkPoints.length >= 2 ? renderSparkline(sparkPoints) : '';

        const fmtVal = (v) => (v === 0 || v === '' || v == null) ? '' : v;
        const wStep = state.session.unit === 'lb' ? 5 : 2.5;

        const setsHTML = ex.sets.map((set, sIdx) => {
          const type = set.type || 'normal';
          const tag = SET_TYPE_TAGS[type] || '';
          const typeLabel = SET_TYPE_LABELS[type] || 'Normal';
          const prev = sIdx > 0 ? ex.sets[sIdx - 1] : null;
          const restChip = (prev && prev.restSeconds > 0) ? `
            <div class="wt-ex__rest-chip" aria-label="Rest before set ${sIdx + 1}: ${fmtRestShort(prev.restSeconds)}">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8"/><polyline points="12 9 12 13 15 15"/></svg>
              Rest ${fmtRestShort(prev.restSeconds)}
            </div>` : '';

          if (isCardio) {
            const timeVal = set.time != null ? set.time : (set.reps || '');
            const distVal = set.distance != null ? set.distance : (set.weight || '');
            const lastTime = last ? (last.time != null ? last.time : last.reps) : '';
            const lastDist = last ? (last.distance != null ? last.distance : last.weight) : '';
            return `
              ${restChip}
              <div class="wt-ex__set wt-ex__set--cardio" data-set-idx="${sIdx}">
                <button type="button" class="wt-ex__set-num wt-ex__set-num--${type}" data-action="cycle-type" aria-label="Set ${sIdx + 1}, ${typeLabel}. Tap to change set type.">
                  <span class="wt-ex__set-num-n">${sIdx + 1}</span>
                  ${tag ? `<span class="wt-ex__set-num-tag" aria-hidden="true">${tag}</span>` : ''}
                </button>
                <div class="wt-ex__weight">
                  <button type="button" class="wt-ex__step" data-action="time-down" data-step="1" aria-label="Decrease time">−</button>
                  <input type="number" inputmode="numeric" step="1" min="0" placeholder="${lastTime ? lastTime : '20'}" value="${fmtVal(timeVal)}" data-field="time" aria-label="Set ${sIdx + 1} time in minutes">
                  <button type="button" class="wt-ex__step" data-action="time-up" data-step="1" aria-label="Increase time">+</button>
                </div>
                <input type="number" inputmode="decimal" step="0.1" min="0" placeholder="${lastDist ? lastDist : '0.0'}" value="${fmtVal(distVal)}" data-field="distance" aria-label="Set ${sIdx + 1} distance in ${distUnit}">
                <button type="button" class="wt-ex__set-check ${set.completed ? 'is-done' : ''}" data-action="check" aria-label="Mark set complete" aria-pressed="${set.completed ? 'true' : 'false'}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </button>
              </div>
            `;
          }

          return `
            ${restChip}
            <div class="wt-ex__set" data-set-idx="${sIdx}">
              <button type="button" class="wt-ex__set-num wt-ex__set-num--${type}" data-action="cycle-type" aria-label="Set ${sIdx + 1}, ${typeLabel}. Tap to change set type.">
                <span class="wt-ex__set-num-n">${sIdx + 1}</span>
                ${tag ? `<span class="wt-ex__set-num-tag" aria-hidden="true">${tag}</span>` : ''}
              </button>
              <div class="wt-ex__weight">
                <button type="button" class="wt-ex__step" data-action="weight-down" data-step="${wStep}" aria-label="Decrease weight">−</button>
                <input type="number" inputmode="decimal" step="${wStep}" min="0" placeholder="${last && last.weight ? last.weight : '0'}" value="${fmtVal(set.weight)}" data-field="weight" aria-label="Set ${sIdx + 1} weight">
                <button type="button" class="wt-ex__step" data-action="weight-up" data-step="${wStep}" aria-label="Increase weight">+</button>
              </div>
              <input type="number" inputmode="numeric" step="1" min="0" pattern="[0-9]*" placeholder="${last && last.reps ? last.reps : '0'}" value="${fmtVal(set.reps)}" data-field="reps" aria-label="Set ${sIdx + 1} reps">
              <button type="button" class="wt-ex__set-check ${set.completed ? 'is-done' : ''}" data-action="check" aria-label="Mark set complete" aria-pressed="${set.completed ? 'true' : 'false'}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
            </div>
          `;
        }).join('');

        // Compact summary shown when collapsed: "3 sets · 2 ✓"
        const completedCount = ex.sets.filter((s) => s.completed).length;
        const summaryHTML = `
          <span class="wt-ex__summary">
            ${ex.sets.length} ${ex.sets.length === 1 ? 'set' : 'sets'}
            ${completedCount > 0 ? `· <span class="wt-ex__summary-done">${completedCount}&nbsp;✓</span>` : ''}
          </span>
        `;

        const setHeadHTML = isCardio
          ? `<span>Set</span><span>Time (min)</span><span>Distance (${escapeHTML(distUnit)})</span><span></span>`
          : `<span>Set</span><span>Weight (${escapeHTML(state.session.unit)})</span><span>Reps</span><span></span>`;

        card.innerHTML = `
          <div class="wt-ex__head" data-action="toggle" role="button" tabindex="0" aria-expanded="${collapsed ? 'false' : 'true'}">
            <div class="wt-ex__head-text">
              <span class="wt-ex__name">${escapeHTML(ex.name)}</span>
              <span class="wt-ex__muscle">${escapeHTML(ex.muscle)}</span>
            </div>
            ${summaryHTML}
            <svg class="wt-ex__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            <button type="button" class="wt-ex__remove" data-action="remove-ex" data-stop-toggle aria-label="Remove ${escapeHTML(ex.name)}">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="wt-ex__body">
            ${intelHTML}
            ${useLastHTML}
            ${sparkHTML ? `<div class="wt-ex__last-row">${sparkHTML}</div>` : ''}
            <div class="wt-ex__set-head" aria-hidden="true">
              ${setHeadHTML}
            </div>
            <div class="wt-ex__sets">${setsHTML}</div>
            <div class="wt-ex__set-actions">
              <button type="button" class="wt-ex__set-add" data-action="add-set">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                Add set
              </button>
              <button type="button" class="wt-ex__set-remove" data-action="remove-set" ${ex.sets.length <= 1 ? 'disabled' : ''}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Remove last
              </button>
            </div>
          </div>
        `;
        exercisesEl.appendChild(card);

        // AX3: keyboard support on the exercise head (role="button" tabindex="0").
        const headEl = card.querySelector('.wt-ex__head');
        if (headEl) {
          headEl.addEventListener('keydown', (ev) => {
            if (ev.target !== headEl) return;
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              headEl.click();
            }
          });
        }

        // Wire up set inputs — select-all on focus for fast overwrite on mobile
        $$('.wt-ex__set input', card).forEach((input) => {
          input.addEventListener('focus', () => {
            setTimeout(() => input.select(), 0);
          });
          input.addEventListener('input', () => {
            const setIdx = +input.closest('.wt-ex__set').dataset.setIdx;
            const field = input.dataset.field;
            const v = parseFloat(input.value);
            const val = Number.isFinite(v) ? v : 0;
            if (field === 'time') {
              ex.sets[setIdx].time = val;
              ex.sets[setIdx].reps = val;
            } else if (field === 'distance') {
              ex.sets[setIdx].distance = val;
              ex.sets[setIdx].weight = val;
              ex.sets[setIdx].distanceUnit = distUnit;
            } else {
              ex.sets[setIdx][field] = val;
            }
          });
        });
        // Wire actions (including weight and time steppers)
        card.querySelectorAll('[data-action]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            const action = btn.dataset.action;
            if (btn.hasAttribute('data-stop-toggle')) e.stopPropagation();
            if (action === 'toggle') {
              ex._collapsed = !ex._collapsed;
              card.classList.toggle('is-collapsed', ex._collapsed);
              const head = card.querySelector('.wt-ex__head');
              if (head) head.setAttribute('aria-expanded', ex._collapsed ? 'false' : 'true');
              return;
            }
            if (action === 'remove-ex') {
              state.session.exercises.splice(exIdx, 1);
              renderExercises();
            } else if (action === 'add-set') {
              if (isCardio) {
                ex.sets.push({ time: 0, distance: 0, reps: 0, weight: 0, distanceUnit: distUnit, unit: state.session.unit, completed: false, type: 'normal', restSeconds: 0, completedAt: 0 });
              } else {
                ex.sets.push({ weight: 0, reps: 0, unit: state.session.unit, completed: false, type: 'normal', restSeconds: 0, completedAt: 0 });
              }
              renderExercises();
            } else if (action === 'remove-set') {
              if (ex.sets.length > 1) { ex.sets.pop(); renderExercises(); }
            } else if (action === 'check') {
              const setRow = btn.closest('.wt-ex__set');
              const setIdx = +setRow.dataset.setIdx;
              const setObj = ex.sets[setIdx];

              if (isCardio) {
                const tInput = setRow.querySelector('input[data-field="time"]');
                const dInput = setRow.querySelector('input[data-field="distance"]');
                if (!setObj.completed && (!setObj.time && !setObj.distance && !setObj.weight && !setObj.reps)) {
                  const fillT = (suggest && suggest.time) || (last && (last.time != null ? last.time : last.reps)) || 20;
                  const fillD = (suggest && suggest.distance) || (last && (last.distance != null ? last.distance : last.weight)) || 0;
                  setObj.time     = fillT;
                  setObj.reps     = fillT;
                  setObj.distance = fillD;
                  setObj.weight   = fillD;
                  setObj.distanceUnit = distUnit;
                  if (tInput) tInput.value = fillT || '';
                  if (dInput) dInput.value = fillD || '';
                } else {
                  if (tInput && tInput.value) { setObj.time = parseFloat(tInput.value) || 0; setObj.reps = setObj.time; }
                  if (dInput && dInput.value) { setObj.distance = parseFloat(dInput.value) || 0; setObj.weight = setObj.distance; }
                  setObj.distanceUnit = distUnit;
                }
              } else {
                const wInput = setRow.querySelector('input[data-field="weight"]');
                const rInput = setRow.querySelector('input[data-field="reps"]');
                if (!setObj.completed && (!setObj.weight && !setObj.reps)) {
                  const fillW = (suggest && suggest.weight) || (last && last.weight) || 0;
                  const fillR = (suggest && suggest.reps)   || (last && last.reps)   || 0;
                  setObj.weight = fillW;
                  setObj.reps   = fillR;
                  if (wInput) wInput.value = fillW || '';
                  if (rInput) rInput.value = fillR || '';
                }
              }

              setObj.completed = !setObj.completed;
              setObj.unit = state.session.unit;
              btn.classList.toggle('is-done', setObj.completed);
              btn.setAttribute('aria-pressed', setObj.completed ? 'true' : 'false');

              if (setObj.completed) {
                setRow.classList.remove('is-just-saved');
                void setRow.offsetWidth;
                setRow.classList.add('is-just-saved');
              }

              if (setObj.completed) {
                const now = Date.now();
                setObj.completedAt = now;
                for (let i = setIdx - 1; i >= 0; i--) {
                  const prev = ex.sets[i];
                  if (prev.completed && prev.completedAt) {
                    const gap = Math.round((now - prev.completedAt) / 1000);
                    prev.restSeconds = Math.min(gap, 1800);
                    const sibling = setRow.previousElementSibling;
                    const chipHTML = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8"/><polyline points="12 9 12 13 15 15"/></svg> Rest ' + fmtRestShort(prev.restSeconds);
                    if (sibling && sibling.classList.contains('wt-ex__rest-chip')) {
                      sibling.innerHTML = chipHTML;
                      sibling.setAttribute('aria-label', 'Rest before set ' + (setIdx + 1) + ': ' + fmtRestShort(prev.restSeconds));
                    } else {
                      const chip = document.createElement('div');
                      chip.className = 'wt-ex__rest-chip';
                      chip.setAttribute('aria-label', 'Rest before set ' + (setIdx + 1) + ': ' + fmtRestShort(prev.restSeconds));
                      chip.innerHTML = chipHTML;
                      setRow.parentNode.insertBefore(chip, setRow);
                    }
                    break;
                  }
                }
              } else {
                setObj.completedAt = 0;
              }
              showToast(setObj.completed ? 'Set saved' : 'Set unchecked');
              if (setObj.completed) startRestTimer();
              persistDraft();
              updateStickyBar();
            } else if (action === 'time-up' || action === 'time-down') {
              const setRow = btn.closest('.wt-ex__set');
              const setIdx = +setRow.dataset.setIdx;
              const input  = setRow.querySelector('input[data-field="time"]');
              const step   = parseFloat(btn.dataset.step) || 1;
              const direction = action === 'time-up' ? 1 : -1;
              const current = parseFloat(input.value) || (last && (last.time != null ? last.time : last.reps)) || 0;
              const next = Math.max(0, +(current + step * direction).toFixed(1));
              input.value = next || '';
              ex.sets[setIdx].time = next;
              ex.sets[setIdx].reps = next;
              persistDraft();
            } else if (action === 'weight-up' || action === 'weight-down') {
              const setRow = btn.closest('.wt-ex__set');
              const setIdx = +setRow.dataset.setIdx;
              const input  = setRow.querySelector('input[data-field="weight"]');
              const step   = parseFloat(btn.dataset.step) || 2.5;
              const direction = action === 'weight-up' ? 1 : -1;
              const current = parseFloat(input.value) || (last && last.weight) || 0;
              const next = Math.max(0, +(current + step * direction).toFixed(2));
              input.value = next;
              ex.sets[setIdx].weight = next;
              persistDraft();
            } else if (action === 'use-last' && last) {
              ex.sets.forEach((set) => {
                if (isCardio) {
                  const t = last.time != null ? last.time : (last.reps || 0);
                  const d = last.distance != null ? last.distance : (last.weight || 0);
                  set.time         = t;
                  set.reps         = t;
                  set.distance     = d;
                  set.weight       = d;
                  set.distanceUnit = distUnit;
                  set.unit         = state.session.unit;
                } else {
                  set.weight = last.weight || 0;
                  set.reps   = last.reps   || 0;
                  set.unit   = state.session.unit;
                }
              });
              renderExercises();
            } else if (action === 'cycle-type') {
              const setRow = btn.closest('.wt-ex__set');
              const setIdx = +setRow.dataset.setIdx;
              const setObj = ex.sets[setIdx];
              const cur = setObj.type || 'normal';
              const nextIdx = (SET_TYPES.indexOf(cur) + 1) % SET_TYPES.length;
              setObj.type = SET_TYPES[nextIdx];
              showToast(SET_TYPE_LABELS[setObj.type]);
              btn.classList.remove('wt-ex__set-num--normal', 'wt-ex__set-num--warmup', 'wt-ex__set-num--drop', 'wt-ex__set-num--failure');
              btn.classList.add('wt-ex__set-num--' + setObj.type);
              btn.setAttribute('aria-label', 'Set ' + (setIdx + 1) + ', ' + SET_TYPE_LABELS[setObj.type] + '. Tap to change set type.');
              const numEl = btn.querySelector('.wt-ex__set-num-n');
              btn.innerHTML = (numEl ? numEl.outerHTML : '<span class="wt-ex__set-num-n">' + (setIdx + 1) + '</span>')
                + (SET_TYPE_TAGS[setObj.type] ? '<span class="wt-ex__set-num-tag" aria-hidden="true">' + SET_TYPE_TAGS[setObj.type] + '</span>' : '');
              persistDraft();
            }
          });
        });
      });
      finishBtn.disabled = false;
      // Sync sticky bottom action bar visibility
      updateStickyBar();
      // Save the working state as a draft so refreshes don't lose work
      persistDraft();
    }

    // Mini sparkline (inline SVG, no library)
    function renderSparkline(points) {
      const w = 64, h = 22, pad = 2;
      const values = points.map((p) => p.value);
      const min = Math.min.apply(null, values);
      const max = Math.max.apply(null, values);
      const range = max - min || 1;
      const xStep = (w - pad * 2) / (values.length - 1);
      const path = values.map((v, i) => {
        const x = pad + i * xStep;
        const y = h - pad - ((v - min) / range) * (h - pad * 2);
        return (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
      }).join(' ');
      const lastIdx = values.length - 1;
      const lastX = pad + lastIdx * xStep;
      const lastY = h - pad - ((values[lastIdx] - min) / range) * (h - pad * 2);
      return `
        <svg class="wt-ex__spark" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" aria-hidden="true">
          <path d="${path}" fill="none" stroke="var(--color-primary, #7CFF3A)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="${lastX.toFixed(1)}" cy="${lastY.toFixed(1)}" r="2" fill="var(--color-primary, #7CFF3A)"/>
        </svg>`;
    }

    // ── Exercise picker modal ────────────────────────
    const picker      = $('[data-wt-picker]');
    const pickerBody  = $('[data-wt-picker-body]');
    const pickerSearch= $('[data-wt-picker-search]');
    // Filter state declared HERE (before openPicker) so they are not in
    // the temporal dead zone when openPicker() is called at runtime.
    const pickerFiltersEl  = $('[data-wt-picker-filters]');
    let   activeFilterMuscle = null; // null = show all

    // Move modal directly to document.body so it is never clipped or detached by section transforms
    if (picker && picker.parentNode !== document.body) {
      document.body.appendChild(picker);
    }
    if (picker) {
      picker.addEventListener('touchmove', (e) => {
        if (!e.target.closest('.wt-picker__body') && !e.target.closest('.wt-picker__form') && !e.target.closest('.wt-picker__filters')) {
          e.preventDefault();
        }
      }, { passive: false });
    }

    // AX4: remember the element that opened the dialog so we can return
    // focus to it on close. Without this, keyboard users get teleported
    // to the top of the document every time a modal closes.
    let pickerOpener = null;
    let pickerReleaseTrap = null;
    function openPicker() {
      pickerOpener = document.activeElement;
      picker.hidden = false;
      pickerSearch.value = '';

      // Auto-preselect muscle group if user is already training a muscle in this session
      if (state.session && state.session.exercises && state.session.exercises.length > 0) {
        const lastEx = state.session.exercises[state.session.exercises.length - 1];
        if (lastEx && lastEx.muscle) {
          activeFilterMuscle = lastEx.muscle;
        }
      }

      renderFilterChips();
      renderPicker('');
      setTimeout(() => pickerSearch.focus({ preventScroll: true }), 50);
      lockScroll();
      // Activate focus trap so keyboard users stay within the picker
      if (pickerReleaseTrap) pickerReleaseTrap();
      const pickerPanel = picker.querySelector('.wt-picker__panel');
      if (pickerPanel) pickerReleaseTrap = trapFocus(pickerPanel);
    }
    function closePicker() {
      if (pickerReleaseTrap) { pickerReleaseTrap(); pickerReleaseTrap = null; }
      picker.hidden = true;
      unlockScroll();
      if (pickerOpener && typeof pickerOpener.focus === 'function') {
        try { pickerOpener.focus(); } catch (_) {}
      }
      pickerOpener = null;
    }
    // Distinct list of muscle groups seen across built-in + custom exercises
    function knownMuscles() {
      const set = new Set();
      WD.allExercises().forEach((e) => set.add(e.muscle));
      return Array.from(set).sort();
    }
    // Distinct equipment types
    function knownEquipment() {
      const set = new Set(['Barbell', 'Dumbbell', 'Cable', 'Machine', 'Bodyweight', 'Other']);
      WD.allExercises().forEach((e) => set.add(e.equipment));
      return Array.from(set).sort();
    }

    function pickExercise(ex) {
      // Auto-collapse any existing exercises so the newly-added one is the
      // sole focus. Reduces visual clutter when stacking many exercises.
      state.session.exercises.forEach((e) => { e._collapsed = true; });
      const isCardio = isCardioExercise(ex);
      const distUnit = state.session.unit === 'lb' ? 'mi' : 'km';
      if (ex.muscle) activeFilterMuscle = ex.muscle;
      state.session.exercises.push({
        name: ex.name,
        muscle: ex.muscle,
        equipment: ex.equipment,
        sets: [
          isCardio
            ? { time: 0, distance: 0, reps: 0, weight: 0, distanceUnit: distUnit, unit: state.session.unit, completed: false, type: 'normal', restSeconds: 0, completedAt: 0 }
            : { weight: 0, reps: 0, unit: state.session.unit, completed: false, type: 'normal', restSeconds: 0, completedAt: 0 }
        ],
        notes: '',
        _collapsed: false,
      });
      closePicker();
      renderExercises();
    }

    // ── Muscle-group filter chips ────────────────────────────────────
    // Renders a scrollable horizontal chip row below the search input.
    // (pickerFiltersEl and activeFilterMuscle declared above openPicker.)
    function renderFilterChips() {
      if (!pickerFiltersEl) return;
      const muscles = knownMuscles();
      pickerFiltersEl.innerHTML = [
        { label: 'All', value: null },
        ...muscles.map((m) => ({ label: m, value: m })),
      ].map(({ label, value }) => {
        const isActive = value === activeFilterMuscle;
        return `<button type="button" class="wt-picker__chip${isActive ? ' is-active' : ''}" data-muscle="${escapeHTML(value || '')}" aria-pressed="${isActive}">${escapeHTML(label)}</button>`;
      }).join('');
      // Wire chip clicks
      pickerFiltersEl.querySelectorAll('.wt-picker__chip').forEach((btn) => {
        btn.addEventListener('click', () => {
          const m = btn.dataset.muscle || null;
          activeFilterMuscle = (m === activeFilterMuscle) ? null : m; // toggle
          renderFilterChips();
          renderPicker(pickerSearch.value);
        });
      });
      // Scroll the active chip into view smoothly within the horizontal chip list
      if (activeFilterMuscle) {
        requestAnimationFrame(() => {
          const activeChip = pickerFiltersEl.querySelector('.wt-picker__chip.is-active');
          if (activeChip) {
            activeChip.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
          }
        });
      }
    }

    function renderPicker(query) {
      const q = (query || '').trim().toLowerCase();
      // Always render builtin + custom exercises, plus the "+ Add custom" shortcut
      const all = WD.allExercises();
      const matches = all.filter((ex) => {
        // Muscle filter (null = all)
        if (activeFilterMuscle && ex.muscle !== activeFilterMuscle) return false;
        // Text query
        return !q || ex.name.toLowerCase().includes(q) || ex.muscle.toLowerCase().includes(q) || (ex.equipment || '').toLowerCase().includes(q);
      });

      // Header: always-on "Add custom exercise" CTA. If a search query exists
      // and no built-in matches, also offer to create with that query as the name.
      const customCtaHTML = `
        <div class="wt-picker__group wt-picker__group--cta">
          <button type="button" class="wt-picker__add-cta" data-add-custom>
            <span class="wt-picker__add-cta-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
            </span>
            <span>${q ? `Add "<strong>${escapeHTML(query)}</strong>" as a custom exercise` : 'Add a custom exercise'}</span>
          </button>
        </div>`;

      if (!matches.length) {
        pickerBody.innerHTML = customCtaHTML +
          `<p class="wt-picker__no-results">No exercises match "${escapeHTML(query)}". Add it as a custom exercise above.</p>`;
        wireCustomCta(query);
        return;
      }

      // Group by muscle. Stable order: built-in muscles first, then any custom.
      const groups = {};
      matches.forEach((ex) => { (groups[ex.muscle] = groups[ex.muscle] || []).push(ex); });
      const groupHTML = Object.keys(groups).map((muscle) => `
        <div class="wt-picker__group">
          <p class="wt-picker__group-title">${escapeHTML(muscle)}</p>
          ${groups[muscle].map((ex) => `
            <button type="button" class="wt-picker__item" data-pick='${escapeHTML(JSON.stringify({name: ex.name, muscle: ex.muscle, equipment: ex.equipment || 'Other'}))}'>
              <span class="wt-picker__item-name">
                ${escapeHTML(ex.name)}
                ${ex.custom ? '<span class="wt-picker__item-tag">Custom</span>' : ''}
              </span>
              <span class="wt-picker__item-eq">${escapeHTML(ex.equipment || '—')}</span>
            </button>
          `).join('')}
        </div>
      `).join('');

      pickerBody.innerHTML = customCtaHTML + groupHTML;

      pickerBody.querySelectorAll('[data-pick]').forEach((btn) => {
        btn.addEventListener('click', () => {
          let payload = {};
          try { payload = JSON.parse(btn.dataset.pick); } catch (_) {}
          if (!payload.name) return;
          pickExercise(payload);
        });
      });
      wireCustomCta(query);
    }

    function wireCustomCta(initialName) {
      const cta = pickerBody.querySelector('[data-add-custom]');
      if (!cta) return;
      cta.addEventListener('click', () => showCustomForm(initialName));
    }

    function showCustomForm(initialName) {
      const muscles   = knownMuscles();
      const equipment = knownEquipment();
      pickerBody.innerHTML = `
        <div class="wt-picker__form" role="form" aria-label="New custom exercise">
          <p class="wt-picker__form-title">New custom exercise</p>
          <label class="wt-picker__form-label" for="wt-cust-name">Name</label>
          <input type="text" id="wt-cust-name" class="wt-picker__form-input" placeholder="e.g. Cable wood-chop" value="${escapeHTML(initialName || '')}" autocomplete="off">

          <label class="wt-picker__form-label" for="wt-cust-muscle">Muscle group</label>
          <div class="wt-picker__form-select">
            <select id="wt-cust-muscle">
              ${muscles.map((m) => `<option value="${escapeHTML(m)}">${escapeHTML(m)}</option>`).join('')}
              <option value="Other">Other</option>
            </select>
          </div>

          <label class="wt-picker__form-label" for="wt-cust-equipment">Equipment</label>
          <div class="wt-picker__form-select">
            <select id="wt-cust-equipment">
              ${equipment.map((e) => `<option value="${escapeHTML(e)}">${escapeHTML(e)}</option>`).join('')}
            </select>
          </div>

          <div class="wt-picker__form-error" data-form-error hidden></div>

          <div class="wt-picker__form-actions">
            <button type="button" class="wt-picker__form-btn wt-picker__form-btn--ghost" data-form-cancel>Cancel</button>
            <button type="button" class="wt-picker__form-btn wt-picker__form-btn--primary" data-form-save>Save &amp; add</button>
          </div>
        </div>
      `;

      const nameEl    = pickerBody.querySelector('#wt-cust-name');
      const muscleEl  = pickerBody.querySelector('#wt-cust-muscle');
      const equipEl   = pickerBody.querySelector('#wt-cust-equipment');
      const errorEl   = pickerBody.querySelector('[data-form-error]');
      const saveBtn   = pickerBody.querySelector('[data-form-save]');
      const cancelBtn = pickerBody.querySelector('[data-form-cancel]');

      // Default muscle: try to keep "Other" as fallback; pre-select first
      if (muscles.length) muscleEl.value = muscles[0];

      setTimeout(() => nameEl.focus(), 30);

      function showError(msg) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
      function clearError() { errorEl.hidden = true; errorEl.textContent = ''; }

      function trySave() {
        clearError();
        const name = nameEl.value.trim();
        if (!name) { showError('Name is required.'); return; }
        const created = WD.addCustomExercise({
          name,
          muscle: muscleEl.value || 'Other',
          equipment: equipEl.value || 'Other',
        });
        if (!created) {
          showError('That name is already in your exercise list.');
          return;
        }
        // Add it to the active session immediately
        pickExercise(created);
      }

      saveBtn.addEventListener('click', trySave);
      cancelBtn.addEventListener('click', () => renderPicker(pickerSearch.value));
      nameEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') trySave(); });
    }
    // Bind ALL "add exercise" triggers (inline action + sticky bottom bar)
    $$('[data-wt-add-exercise]').forEach((b) => b.addEventListener('click', openPicker));
    if (picker) {
      picker.querySelectorAll('[data-wt-picker-close]').forEach((b) => b.addEventListener('click', closePicker));
    }
    document.querySelectorAll('[data-wt-picker-close]').forEach((b) => b.addEventListener('click', closePicker));

    // Sticky bottom action bar (mobile only)
    const stickyBar       = $('[data-wt-sticky]');
    const stickyFinishBtn = $('[data-wt-sticky-finish]');
    const stickyMeta      = $('[data-wt-sticky-meta]');
    const durationEl      = $('[data-wt-duration]');

    // ── Session duration clock ───────────────────────────────────────
    // Starts silently when the first exercise is added; displayed in the
    // sticky bottom bar. Simple elapsed-time UX — no server round-trip.
    let sessionStartMs  = 0;
    let durationInterval = null;
    function startDurationClock() {
      if (sessionStartMs > 0) return; // already running
      sessionStartMs = Date.now();
      // Store on root so Theme Editor section:load can clear it on re-init
      if (root.__wtDurationInterval) clearInterval(root.__wtDurationInterval);
      root.__wtDurationInterval = durationInterval = setInterval(() => {
        if (!durationEl) return;
        const elapsedSec = Math.floor((Date.now() - sessionStartMs) / 1000);
        const m = Math.floor(elapsedSec / 60);
        const s = elapsedSec % 60;
        durationEl.textContent = m + ':' + (s < 10 ? '0' : '') + s;
      }, 1000);
    }
    function stopDurationClock() {
      clearInterval(durationInterval);
      if (root.__wtDurationInterval) { clearInterval(root.__wtDurationInterval); root.__wtDurationInterval = null; }
      durationInterval = null;
      sessionStartMs = 0;
      if (durationEl) durationEl.textContent = '0:00';
    }

    function updateStickyBar() {
      if (!stickyBar) return;
      const exCount = state.session.exercises.length;
      const setCount = state.session.exercises.reduce(
        (sum, ex) => sum + ex.sets.filter((s) => s.completed).length, 0
      );
      const visible = exCount > 0;
      stickyBar.classList.toggle('is-visible', visible);
      stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (stickyMeta) stickyMeta.textContent = setCount > 0 ? (setCount + ' set' + (setCount === 1 ? '' : 's')) : '';
      // Start the duration clock on first exercise
      if (visible) startDurationClock();
    }
    if (stickyFinishBtn) {
      // Call finishSession() directly — avoids the broken pattern of
      // clicking a potentially-disabled button programmatically.
      stickyFinishBtn.addEventListener('click', finishSession);
    }
    if (pickerSearch) pickerSearch.addEventListener('input', (e) => renderPicker(e.target.value));

    // ── ROUTINES ──────────────────────────────────────
    // A routine = saved set of exercises + default set count, reusable any day.
    // Stored via WD.saveRoutine / WD.getRoutines / WD.deleteRoutine.
    const routinesListEl   = $('[data-wt-routines-list]');
    const routinesEmptyEl  = $('[data-wt-routines-empty]');
    const routinesCountEl  = $('[data-wt-routines-count]');
    const goLogBtn         = $('[data-wt-routines-go-log]');
    function updateRoutinesCount() {
      if (!routinesCountEl) return;
      const n = WD.getRoutines().length;
      routinesCountEl.textContent = n;
    }
    function renderRoutines() {
      if (!routinesListEl) return;
      const routines = WD.getRoutines();
      updateRoutinesCount();
      if (!routines.length) {
        routinesListEl.innerHTML = '';
        if (routinesEmptyEl) routinesEmptyEl.hidden = false;
        return;
      }
      if (routinesEmptyEl) routinesEmptyEl.hidden = true;
      routinesListEl.innerHTML = routines.map((r) => {
        const totalSets = r.exercises.reduce((sum, e) => sum + (e.defaultSets || 0), 0);
        const lastUsed = r.lastUsedAt
          ? 'Last used ' + relativeTime(r.lastUsedAt)
          : 'Never used';
        const chips = r.exercises.map((e) =>
          '<span class="wt-routine__chip">'
            + escapeHTML(e.name)
            + '<span class="wt-routine__chip-sets">' + (e.defaultSets || 1) + '×</span>'
          + '</span>'
        ).join('');
        return ''
          + '<li class="wt-routine" data-routine-id="' + escapeHTML(r.id) + '">'
            + '<div class="wt-routine__head">'
              + '<h3 class="wt-routine__name">' + escapeHTML(r.name) + '</h3>'
              + '<span class="wt-routine__meta">' + r.exercises.length + ' exercise' + (r.exercises.length === 1 ? '' : 's') + ' · ' + totalSets + ' set' + (totalSets === 1 ? '' : 's') + '</span>'
            + '</div>'
            + '<div class="wt-routine__actions">'
              + '<button type="button" class="wt-routine__btn wt-routine__btn--start" data-routine-action="start" aria-label="Start routine ' + escapeHTML(r.name) + '">'
                + '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="5 3 19 12 5 21 5 3"/></svg>'
                + '<span>Start</span>'
              + '</button>'
              + '<button type="button" class="wt-routine__btn wt-routine__btn--ghost" data-routine-action="delete" aria-label="Delete routine ' + escapeHTML(r.name) + '">'
                + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>'
              + '</button>'
            + '</div>'
            + '<div class="wt-routine__exercises">' + chips + '</div>'
            + '<span class="wt-routine__summary"><strong>' + lastUsed + '</strong></span>'
          + '</li>';
      }).join('');
      // Wire each routine's actions
      routinesListEl.querySelectorAll('.wt-routine').forEach((li) => {
        const id = li.dataset.routineId;
        li.querySelector('[data-routine-action="start"]')?.addEventListener('click', () => startRoutine(id));
        li.querySelector('[data-routine-action="delete"]')?.addEventListener('click', () => removeRoutine(id));
      });
    }

    // Start routine: replace any unsaved current session with the routine's
    // exercises (with empty sets), switch to Log tab, and bump lastUsedAt.
    async function startRoutine(id) {
      const routine = WD.getRoutines().find((r) => r.id === id);
      if (!routine) return;
      // If there are completed sets in the current session, ask before discarding
      const hasProgress = state.session.exercises.some(
        (ex) => ex.sets.some((s) => s.completed || s.weight > 0 || s.reps > 0)
      );
      if (hasProgress) {
        const ok = await wtConfirm('Replace current workout with "' + routine.name + '"?', {
          title: 'Replace workout?',
          ok: 'Replace',
          danger: true,
        });
        if (!ok) return;
      }
      const distUnit = state.session.unit === 'lb' ? 'mi' : 'km';
      state.session.exercises = routine.exercises.map((rex, idx) => {
        const isCardio = isCardioExercise(rex);
        return {
          name: rex.name,
          muscle: rex.muscle || 'Other',
          equipment: rex.equipment || 'Other',
          notes: '',
          // Only the LAST exercise expanded so the active set is always visible
          _collapsed: idx !== routine.exercises.length - 1,
          sets: Array.from({ length: rex.defaultSets || 3 }, () => (
            isCardio
              ? { time: 0, distance: 0, reps: 0, weight: 0, distanceUnit: distUnit, unit: state.session.unit, completed: false, type: 'normal', restSeconds: 0, completedAt: 0 }
              : { weight: 0, reps: 0, unit: state.session.unit, completed: false, type: 'normal', restSeconds: 0, completedAt: 0 }
          )),
        };
      });
      if (routine.exercises && routine.exercises.length > 0) {
        const lastRex = routine.exercises[routine.exercises.length - 1];
        if (lastRex && lastRex.muscle) activeFilterMuscle = lastRex.muscle;
      }
      WD.markRoutineUsed(id);
      // Switch to Log tab and re-render
      $('[data-tab="log"]').click();
      renderExercises();
      persistDraft();
      showToast('Started "' + routine.name + '"');
    }

    async function removeRoutine(id) {
      const r = WD.getRoutines().find((x) => x.id === id);
      if (!r) return;
      const ok = await wtConfirm('Delete routine "' + r.name + '"? This cannot be undone.', {
        title: 'Delete routine?',
        ok: 'Delete',
        danger: true,
      });
      if (!ok) return;
      WD.deleteRoutine(id);
      renderRoutines();
      showToast('Routine deleted');
    }

    if (goLogBtn) {
      goLogBtn.addEventListener('click', () => $('[data-tab="log"]').click());
    }

    // ── Save-as-routine dialog ────────────────────────
    const rdialog        = $('[data-wt-rdialog]');
    const rdialogName    = $('[data-wt-rdialog-name]');
    const rdialogPreview = $('[data-wt-rdialog-preview]');
    const rdialogConfirm = $('[data-wt-rdialog-confirm]');

    // Move modal directly to document.body so it is never clipped or detached by section transforms
    if (rdialog && rdialog.parentNode !== document.body) {
      document.body.appendChild(rdialog);
    }
    if (rdialog) {
      rdialog.addEventListener('touchmove', (e) => {
        if (!e.target.closest('.wt-rdialog__panel')) {
          e.preventDefault();
        }
      }, { passive: false });
    }
    // AX4: focus restoration for the routine dialog.
    let rdialogOpener = null;
    let rdialogReleaseTrap = null;
    function openRoutineDialog() {
      if (!state.session.exercises.length) return;
      rdialogOpener = document.activeElement;
      // Preview the exercises that will be saved (with their current set count)
      rdialogPreview.innerHTML = state.session.exercises.map((ex) =>
        '<span class="wt-rdialog__preview-chip">'
          + escapeHTML(ex.name)
          + '<span class="wt-rdialog__preview-sets">' + ex.sets.length + '×</span>'
        + '</span>'
      ).join('');
      rdialogName.value = '';
      rdialog.hidden = false;
      lockScroll();
      // Activate focus trap within the routine dialog panel
      if (rdialogReleaseTrap) rdialogReleaseTrap();
      const rdPanel = rdialog.querySelector('.wt-rdialog__panel');
      if (rdPanel) rdialogReleaseTrap = trapFocus(rdPanel);
      setTimeout(() => rdialogName.focus({ preventScroll: true }), 50);
    }
    function closeRoutineDialog() {
      if (rdialogReleaseTrap) { rdialogReleaseTrap(); rdialogReleaseTrap = null; }
      rdialog.hidden = true;
      unlockScroll();
      if (rdialogOpener && typeof rdialogOpener.focus === 'function') {
        try { rdialogOpener.focus(); } catch (_) {}
      }
      rdialogOpener = null;
    }
    function confirmSaveRoutine() {
      const name = (rdialogName.value || '').trim();
      if (!name) {
        rdialogName.focus();
        rdialogName.style.borderColor = 'rgba(255,80,90,0.6)';
        setTimeout(() => { rdialogName.style.borderColor = ''; }, 1200);
        return;
      }
      const payload = {
        name: name,
        exercises: state.session.exercises.map((ex) => ({
          name: ex.name,
          muscle: ex.muscle,
          equipment: ex.equipment,
          defaultSets: ex.sets.length,
        })),
      };
      const created = WD.saveRoutine(payload);
      if (!created) {
        showToast('Could not save routine');
        return;
      }
      closeRoutineDialog();
      updateRoutinesCount();
      showToast('Saved routine "' + created.name + '"');
    }
    if (saveRoutineBtn) saveRoutineBtn.addEventListener('click', openRoutineDialog);
    if (rdialog) {
      rdialog.querySelectorAll('[data-wt-rdialog-close]').forEach((b) => b.addEventListener('click', closeRoutineDialog));
    }
    document.querySelectorAll('[data-wt-rdialog-close]').forEach((b) => b.addEventListener('click', closeRoutineDialog));
    if (rdialogConfirm) rdialogConfirm.addEventListener('click', confirmSaveRoutine);
    if (rdialogName) {
      rdialogName.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); confirmSaveRoutine(); }
      });
    }
    // Single document-level Escape handler dismisses whichever modal is on top.
    // Named function stored on root so Theme Editor re-inits can remove the old
    // listener before adding a fresh one (prevents accumulation across reloads).
    if (root.__wtEscHandler) document.removeEventListener('keydown', root.__wtEscHandler);
    root.__wtEscHandler = function wtEscHandler(e) {
      if (e.key !== 'Escape') return;
      if (rdialog && !rdialog.hidden) { closeRoutineDialog(); return; }
      if (picker && !picker.hidden)   { closePicker();        return; }
    };
    document.addEventListener('keydown', root.__wtEscHandler);

    // Compact relative-time helper for routine "last used" labels
    function relativeTime(ms) {
      const diff = Math.max(0, Date.now() - ms);
      const m = Math.round(diff / 60000);
      if (m < 1)    return 'just now';
      if (m < 60)   return m + ' min ago';
      const h = Math.round(m / 60);
      if (h < 24)   return h + ' hr ago';
      const d = Math.round(h / 24);
      if (d < 7)    return d + ' day' + (d === 1 ? '' : 's') + ' ago';
      const w = Math.round(d / 7);
      if (w < 5)    return w + ' wk' + (w === 1 ? '' : 's') + ' ago';
      const mo = Math.round(d / 30);
      if (mo < 12)  return mo + ' mo ago';
      return Math.round(d / 365) + ' yr ago';
    }

    // Init routine count badge on load + react to data changes from anywhere
    updateRoutinesCount();
    window.addEventListener('workout-data:change', () => {
      updateRoutinesCount();
      // If the routines panel is currently visible, refresh it too
      const routinesPanel = root.querySelector('[data-panel="routines"]');
      if (routinesPanel && !routinesPanel.hidden) renderRoutines();
    });

    // ── PR detection (compute BEFORE saving so we know what's new) ──
    function detectPRs(currentSession) {
      const distUnit = currentSession.unit === 'lb' ? 'mi' : 'km';
      const bestBefore = {};
      WD.getSessions().forEach((s) => {
        s.exercises.forEach((ex) => {
          const isCardio = isCardioExercise(ex);
          ex.sets.forEach((set) => {
            if (!set.completed) return;
            if (set.type === 'warmup') return;
            if (isCardio) {
              const t = set.time != null ? set.time : (set.reps || 0);
              const d = set.distance != null ? set.distance : (set.weight || 0);
              if (t <= 0 && d <= 0) return;
              const score = (d * 1000) + t;
              const prev = bestBefore[ex.name];
              if (!prev || score > prev.score) {
                bestBefore[ex.name] = { isCardio: true, score, time: t, distance: d, distUnit: set.distanceUnit || distUnit };
              }
            } else {
              if (!set.weight || !set.reps) return;
              const oneRm = WD.estimated1RM(set.weight, set.reps);
              const prev = bestBefore[ex.name];
              if (!prev || oneRm > prev.oneRm) {
                bestBefore[ex.name] = { isCardio: false, oneRm, weight: set.weight, reps: set.reps, unit: set.unit || s.unit || 'kg' };
              }
            }
          });
        });
      });
      // Walk current session, find sets that beat previous best
      const newPRs = [];
      currentSession.exercises.forEach((ex) => {
        const isCardio = isCardioExercise(ex);
        let bestNow = bestBefore[ex.name] ? Object.assign({}, bestBefore[ex.name]) : null;
        ex.sets.forEach((set) => {
          if (!set.completed) return;
          if (set.type === 'warmup') return;
          if (isCardio) {
            const t = set.time != null ? set.time : (set.reps || 0);
            const d = set.distance != null ? set.distance : (set.weight || 0);
            if (t <= 0 && d <= 0) return;
            const score = (d * 1000) + t;
            if (!bestNow || score > bestNow.score) {
              const isFirst = !bestNow;
              newPRs.push({
                name: ex.name,
                isCardio: true,
                time: t,
                distance: d,
                distUnit: set.distanceUnit || distUnit,
                isFirst,
                previousTime: bestNow ? bestNow.time : 0,
                previousDistance: bestNow ? bestNow.distance : 0,
                score,
              });
              bestNow = { isCardio: true, score, time: t, distance: d, distUnit: set.distanceUnit || distUnit };
            }
          } else {
            if (!set.weight || !set.reps) return;
            const oneRm = WD.estimated1RM(set.weight, set.reps);
            if (!bestNow || oneRm > bestNow.oneRm) {
              const isFirst = !bestNow;
              newPRs.push({
                name: ex.name,
                isCardio: false,
                weight: set.weight, reps: set.reps,
                unit: set.unit || currentSession.unit,
                oneRm, isFirst,
                previousOneRm: bestNow ? bestNow.oneRm : 0,
              });
              bestNow = { isCardio: false, oneRm, weight: set.weight, reps: set.reps, unit: set.unit || currentSession.unit };
            }
          }
        });
      });
      // Dedupe — keep only the BEST PR per exercise
      const byEx = {};
      newPRs.forEach((p) => {
        if (p.isCardio) {
          if (!byEx[p.name] || p.score > byEx[p.name].score) byEx[p.name] = p;
        } else {
          if (!byEx[p.name] || p.oneRm > byEx[p.name].oneRm) byEx[p.name] = p;
        }
      });
      return Object.values(byEx);
    }

    function showPRCelebration(prs) {
      if (!prs || !prs.length) return;
      const overlay = document.createElement('div');
      overlay.className = 'wt-pr-celebrate';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-label', prs.length + ' new personal record' + (prs.length === 1 ? '' : 's'));
      overlay.innerHTML = `
        <div class="wt-pr-celebrate__backdrop" data-pr-close></div>
        <div class="wt-pr-celebrate__panel">
          <div class="wt-pr-celebrate__halo" aria-hidden="true"></div>

          <div class="wt-pr-celebrate__header">
            <div class="wt-pr-celebrate__icon-box" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                <path d="M4 22h16"/>
                <path d="M10 14.66V17c0 .55-.45 1-1 1H7v4h10v-4h-2c-.55 0-1-.45-1-1v-2.34"/>
                <path d="M18 4H6v7a6 6 0 0 0 12 0V4z"/>
              </svg>
            </div>
            <div class="wt-pr-celebrate__eyebrow-pill">
              <span class="wt-pr-celebrate__dot"></span>
              <span class="wt-pr-celebrate__eyebrow">Personal Record${prs.length > 1 ? 's' : ''} Achieved</span>
            </div>
            <h3 class="wt-pr-celebrate__title">${prs.length === 1 ? 'PR Unlocked' : prs.length + ' PRs Smashed'}</h3>
            <p class="wt-pr-celebrate__subtitle">Outstanding work. Your new peak benchmarks are locked into your history.</p>
          </div>

          <ul class="wt-pr-celebrate__list">
            ${prs.map((p) => {
              if (p.isCardio) {
                const detail = `${p.time} min${p.distance > 0 ? ' · ' + p.distance + ' ' + escapeHTML(p.distUnit) : ''}`;
                const prevDetail = p.previousTime > 0 ? `${p.previousTime} min${p.previousDistance > 0 ? ' · ' + p.previousDistance + ' ' + escapeHTML(p.distUnit) : ''}` : '';
                return `
                  <li class="wt-pr-celebrate__item">
                    <div class="wt-pr-celebrate__item-top">
                      <span class="wt-pr-celebrate__item-name">${escapeHTML(p.name)}</span>
                      <span class="wt-pr-celebrate__tag">${p.isFirst ? 'First Log' : 'New Best'}</span>
                    </div>
                    <div class="wt-pr-celebrate__item-stats">
                      <span class="wt-pr-celebrate__item-detail">${detail}</span>
                      <span class="wt-pr-celebrate__item-meta">
                        ${p.isFirst ? '✨ Baseline logged' : '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg> ' + prevDetail + ' → ' + detail}
                      </span>
                    </div>
                  </li>
                `;
              }
              return `
                <li class="wt-pr-celebrate__item">
                  <div class="wt-pr-celebrate__item-top">
                    <span class="wt-pr-celebrate__item-name">${escapeHTML(p.name)}</span>
                    <span class="wt-pr-celebrate__tag">${p.isFirst ? 'First Log' : 'New Best'}</span>
                  </div>
                  <div class="wt-pr-celebrate__item-stats">
                    <span class="wt-pr-celebrate__item-detail">${p.weight} <span class="wt-pr-celebrate__unit">${escapeHTML(p.unit)}</span> <span class="wt-pr-celebrate__reps">× ${p.reps}</span></span>
                    <span class="wt-pr-celebrate__item-meta">
                      ${p.isFirst ? '✨ Baseline logged' : '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg> 1RM: ' + p.previousOneRm + ' ' + escapeHTML(p.unit) + ' → ' + p.oneRm + ' ' + escapeHTML(p.unit)}
                    </span>
                  </div>
                </li>
              `;
            }).join('')}
          </ul>
          <button type="button" class="wt-pr-celebrate__close" data-pr-close>
            <span>View in History</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
          </button>
        </div>
      `;
      document.body.appendChild(overlay);
      lockScroll();
      requestAnimationFrame(() => overlay.classList.add('is-open'));
      const close = () => {
        overlay.classList.remove('is-open');
        unlockScroll();
        setTimeout(() => overlay.remove(), 300);
        const histTab = $('[data-tab="history"]');
        if (histTab) histTab.click();
      };
      overlay.querySelectorAll('[data-pr-close]').forEach((el) => el.addEventListener('click', close));
      document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
      });
    }

    // ── Finish session ───────────────────────────────
    function finishSession() {
      if (!state.session.exercises.length) return; // guard: nothing to save
      // Auto-mark non-empty sets as completed before saving
      state.session.exercises.forEach((ex) => {
        const isCardio = isCardioExercise(ex);
        ex.sets.forEach((set) => {
          if (!set.completed) {
            if (isCardio) {
              if ((set.time != null && set.time > 0) || (set.distance != null && set.distance > 0) || (set.weight != null && set.weight > 0) || (set.reps != null && set.reps > 0)) {
                set.completed = true;
              }
            } else {
              if ((set.weight != null && set.weight > 0) || (set.reps != null && set.reps > 0)) {
                set.completed = true;
              }
            }
          }
        });
      });
      // Detect PRs BEFORE saving (so we compare against prior history)
      const prs = detectPRs(state.session);
      // Read optional session name
      const sessionNameInput = $('[data-wt-session-name]');
      const sessionName = sessionNameInput ? sessionNameInput.value.trim() : '';
      WD.saveSession({
        date: new Date(state.session.date + 'T12:00:00').toISOString(),
        exercises: state.session.exercises,
        unit: state.session.unit,
        name: sessionName || undefined,
      });
      // Reset form + clear the in-progress draft now that it's committed
      state.session = { date: todayISO(), exercises: [], unit: state.session.unit, name: '' };
      if (dateInput) dateInput.value = state.session.date;
      updateDatePill();
      // Reset session name field
      if (sessionNameInput) sessionNameInput.value = '';
      clearDraft();
      // Stop any rest timer + duration clock that was still running
      skipTimer();
      stopDurationClock();
      renderExercises();
      updateHistoryCount();
      // Force-hide the sticky bar immediately
      const stickyBarEl = root.querySelector('[data-wt-sticky]');
      if (stickyBarEl) {
        stickyBarEl.classList.remove('is-visible');
        stickyBarEl.setAttribute('aria-hidden', 'true');
      }
      showToast('Workout saved');
      // Show celebration before switching tabs (better dopamine moment)
      if (prs.length) {
        showPRCelebration(prs);
      } else {
        $('[data-tab="history"]').click();
      }
    }
    finishBtn.addEventListener('click', finishSession);

    // ── History panel ────────────────────────────────
    const historyEl     = $('[data-wt-history]');
    const historyCount  = $('[data-wt-history-count]');
    function updateHistoryCount() { historyCount.textContent = WD.getSessions().length; }
    function renderHistory() {
      const sessions = WD.getSessions();
      if (!sessions.length) {
        historyEl.innerHTML = `
          <div class="wt__empty">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <p>No sessions logged yet. Finish your first workout to see it here.</p>
          </div>`;
        return;
      }
      historyEl.innerHTML = sessions.map((s) => {
        const totalSets = s.exercises.reduce((sum, e) => sum + e.sets.filter((x) => x.completed).length, 0);
        const volume = WD.sessionVolume(s);
        const distUnit = s.unit === 'lb' ? 'mi' : 'km';
        const exHTML = s.exercises.map((ex) => {
          const isCardio = isCardioExercise(ex);
          const setsList = ex.sets.filter((x) => x.completed).map((set) => {
            if (isCardio) {
              const t = set.time != null ? set.time : (set.reps || 0);
              const d = set.distance != null ? set.distance : (set.weight || 0);
              const du = set.distanceUnit || distUnit;
              let txt = `${t} min`;
              if (d > 0) txt += ` · ${d} ${du}`;
              return `<span class="wt-hist__set-pill wt-hist__set-pill--cardio">${escapeHTML(txt)}</span>`;
            }
            return `<span class="wt-hist__set-pill">${set.weight || 0} ${escapeHTML(set.unit || s.unit || 'kg')} × ${set.reps || 0}</span>`;
          }).join('');
          return `
            <div class="wt-hist__ex">
              <p class="wt-hist__ex-name">
                ${escapeHTML(ex.name)}
                ${isCardio ? '<span class="wt-ex__muscle" style="margin-left:6px;font-size:0.6rem;">Cardio</span>' : ''}
              </p>
              <div class="wt-hist__ex-sets">
                ${setsList || '<span class="wt-hist__set-pill" style="opacity:.5">No completed sets</span>'}
              </div>
            </div>
          `;
        }).join('');
        return `
          <article class="wt-hist" data-id="${escapeHTML(s.id)}">
            <button type="button" class="wt-hist__head" data-toggle aria-expanded="false">
              <span class="wt-hist__title-group">
                ${s.name ? `<span class="wt-hist__name">${escapeHTML(s.name)}</span>` : ''}
                <span class="wt-hist__date${s.name ? ' wt-hist__date--sub' : ''}">${formatDate(s.date)}</span>
              </span>
              <span class="wt-hist__meta">
                <span><strong>${s.exercises.length}</strong> ex</span>
                <span><strong>${totalSets}</strong> sets</span>
                <span><strong>${volume.toLocaleString()}</strong> ${escapeHTML(s.unit || 'kg')}·vol</span>
                <svg class="wt-hist__caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </button>
            <div class="wt-hist__body">
              ${exHTML}
              <div class="wt-hist__actions">
                <button type="button" class="wt__btn wt__btn--danger" data-delete>Delete session</button>
              </div>
            </div>
          </article>
        `;
      }).join('');

      $$('.wt-hist', historyEl).forEach((card) => {
        card.querySelector('[data-toggle]').addEventListener('click', () => {
          card.classList.toggle('is-open');
          const expanded = card.classList.contains('is-open');
          card.querySelector('[data-toggle]').setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
        card.querySelector('[data-delete]').addEventListener('click', async () => {
          const ok = await wtConfirm('Delete this session permanently?', {
            title: 'Delete session?',
            ok: 'Delete',
            danger: true,
          });
          if (!ok) return;
          WD.deleteSession(card.dataset.id);
          renderHistory();
          updateHistoryCount();
        });
      });
    }

    // ── Export handlers — open in Notes / Notepad / Excel ────────
    function downloadFile(filename, content, mime) {
      const blob = new Blob([content], { type: mime + ';charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
    function todayStamp() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate());
    }
    // Pretty labels for exports (also used to translate set.type → text)
    const SET_TYPE_EXPORT = { warmup: 'Warmup', drop: 'Drop set', failure: 'Failure', normal: 'Normal' };
    function fmtRestExport(s) {
      s = Math.max(0, Math.round(s || 0));
      if (s === 0) return '—';
      const m = Math.floor(s / 60); const sec = s % 60;
      return m > 0 ? (m + 'm ' + (sec ? sec + 's' : '').trim()).trim() : (sec + 's');
    }
    function buildTXT() {
      const sessions = WD.getSessions();
      const bw       = WD.getBodyWeight();
      let out = 'AminoBae Workout Log — exported ' + new Date().toLocaleString() + '\n';
      out += '='.repeat(60) + '\n\n';
      if (!sessions.length && !bw.length) return out + 'No data yet.\n';
      sessions.forEach((s) => {
        const d = new Date(s.date);
        const distUnit = s.unit === 'lb' ? 'mi' : 'km';
        out += d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) + '\n';
        out += '-'.repeat(40) + '\n';
        s.exercises.forEach((ex) => {
          const isCardio = isCardioExercise(ex);
          out += ex.name + ' (' + ex.muscle + ')\n';
          ex.sets.forEach((set, i) => {
            if (!set.completed) return;
            const type = SET_TYPE_EXPORT[set.type || 'normal'] || 'Normal';
            const rest = fmtRestExport(set.restSeconds);
            const tag  = type === 'Normal' ? '' : '  [' + type + ']';
            const restTxt = rest !== '—' ? '   rest after: ' + rest : '';
            if (isCardio) {
              const t = set.time != null ? set.time : (set.reps || 0);
              const dVal = set.distance != null ? set.distance : (set.weight || 0);
              const du = set.distanceUnit || distUnit;
              out += '  Set ' + (i + 1) + ':  '
                  + t + ' min' + (dVal > 0 ? ' · ' + dVal + ' ' + du : '')
                  + tag
                  + restTxt
                  + '\n';
            } else {
              out += '  Set ' + (i + 1) + ':  '
                  + (set.weight || 0) + ' ' + (set.unit || s.unit || 'kg')
                  + ' x ' + (set.reps || 0)
                  + tag
                  + restTxt
                  + '\n';
            }
          });
          out += '\n';
        });
        out += 'Total volume: ' + WD.sessionVolume(s) + ' ' + (s.unit || 'kg') + ' (warmups and cardio excluded)\n\n';
      });
      if (bw.length) {
        out += '\n' + '='.repeat(60) + '\n';
        out += 'BODYWEIGHT LOG\n';
        out += '='.repeat(60) + '\n';
        bw.forEach((b) => {
          out += b.date + '   ' + b.weight + ' ' + (b.unit || 'kg') + '\n';
        });
      }
      return out;
    }
    function buildCSV() {
      const sessions = WD.getSessions();
      let rows = [['Date', 'Exercise', 'Muscle', 'Set', 'Type', 'Weight / Dist', 'Reps / Time', 'Unit', 'Rest (sec)', 'Est. 1RM', 'Completed']];
      sessions.forEach((s) => {
        const dateStr = new Date(s.date).toISOString().slice(0, 10);
        const distUnit = s.unit === 'lb' ? 'mi' : 'km';
        s.exercises.forEach((ex) => {
          const isCardio = isCardioExercise(ex);
          ex.sets.forEach((set, i) => {
            const type = SET_TYPE_EXPORT[set.type || 'normal'] || 'Normal';
            if (isCardio) {
              const t = set.time != null ? set.time : (set.reps || 0);
              const dVal = set.distance != null ? set.distance : (set.weight || 0);
              rows.push([
                dateStr, ex.name, ex.muscle, i + 1, type,
                dVal > 0 ? dVal : '-',
                t > 0 ? t : '-',
                dVal > 0 ? (set.distanceUnit || distUnit) : 'min',
                Math.max(0, Math.round(set.restSeconds || 0)),
                '',
                set.completed ? 'Y' : 'N',
              ]);
            } else {
              const oneRm = (set.completed && set.weight && set.reps && type !== 'Warmup')
                ? WD.estimated1RM(set.weight, set.reps) : '';
              rows.push([
                dateStr, ex.name, ex.muscle, i + 1, type,
                set.weight || 0, set.reps || 0,
                set.unit || s.unit || 'kg',
                Math.max(0, Math.round(set.restSeconds || 0)),
                oneRm,
                set.completed ? 'Y' : 'N',
              ]);
            }
          });
        });
      });
      // Bodyweight as a separate "BODYWEIGHT" pseudo-exercise
      WD.getBodyWeight().forEach((b) => {
        rows.push([b.date, 'BODYWEIGHT', '-', 1, '-', b.weight, '-', b.unit || 'kg', 0, '', 'Y']);
      });
      // CSV escape — wrap field in quotes if contains comma/quote/newline
      const esc = (v) => {
        const s = String(v);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      return rows.map((r) => r.map(esc).join(',')).join('\n');
    }
    $('[data-wt-export-txt]')?.addEventListener('click', () => {
      downloadFile('aminobae-workout-' + todayStamp() + '.txt', buildTXT(), 'text/plain');
      showToast('Saved as .txt');
    });
    $('[data-wt-export-csv]')?.addEventListener('click', () => {
      downloadFile('aminobae-workout-' + todayStamp() + '.csv', WD.exportCSV ? WD.exportCSV() : buildCSV(), 'text/csv');
      showToast('Saved as .csv');
    });
    $('[data-wt-export-json]')?.addEventListener('click', () => {
      downloadFile('aminobae-workout-' + todayStamp() + '.json', WD.exportJSON(), 'application/json');
      showToast('Saved as .json');
    });

    // ── Download Demo / Template CSV ──────────────────
    document.querySelectorAll('[data-wt-demo-csv]').forEach((btn) => {
      btn.addEventListener('click', () => {
        downloadFile('aminobae-workout-template.csv', WD.getDemoCSV(), 'text/csv');
        showToast('Demo template downloaded!');
      });
    });

    // ── Import CSV File (Workouts & Routines) ──────────
    const importCsvBtn  = $('[data-wt-import-csv-btn]');
    const importCsvFile = $('[data-wt-import-csv-file]');
    if (importCsvBtn && importCsvFile) {
      importCsvBtn.addEventListener('click', () => {
        importCsvFile.value = '';
        importCsvFile.click();
      });
      importCsvFile.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
          try {
            const text = ev.target.result;
            const res = WD.importCSV(text);
            importCsvFile.value = '';
            if (res && res.success) {
              showToast(`Imported ${res.sessionsCount} session(s) & ${res.exercisesCount} exercise(s)!`);
              renderHistory();
              updateHistoryCount();
              renderRoutines();
              renderBodyWeight();
              renderHeroStats();
              // Automatically switch to history tab so the user sees their imported data
              const historyTab = $('[data-tab="history"]');
              if (historyTab) historyTab.click();
            } else {
              showToast(res && res.error ? res.error : 'Could not parse CSV file. Check format.');
            }
          } catch (err) {
            console.error('Import CSV error:', err);
            showToast('Error reading CSV file');
            importCsvFile.value = '';
          }
        };
        reader.onerror = () => {
          showToast('Failed to read CSV file');
          importCsvFile.value = '';
        };
        reader.readAsText(file);
      });
    }

    // ── Import JSON Backup ───────────────────────────
    const importBtn  = $('[data-wt-import-btn]');
    const importFile = $('[data-wt-import-file]');
    if (importBtn && importFile) {
      importBtn.addEventListener('click', () => {
        importFile.value = '';
        importFile.click();
      });
      importFile.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
          try {
            const text = ev.target.result;
            const success = WD.importJSON(text);
            importFile.value = '';
            if (success) {
              showToast('Backup restored successfully!');
              renderHistory();
              updateHistoryCount();
              renderRoutines();
              renderBodyWeight();
              renderHeroStats();
              const historyTab = $('[data-tab="history"]');
              if (historyTab) historyTab.click();
            } else {
              showToast('Invalid backup file');
            }
          } catch (err) {
            console.error('Import JSON error:', err);
            showToast('Error reading backup file');
            importFile.value = '';
          }
        };
        reader.onerror = () => {
          showToast('Failed to read backup file');
          importFile.value = '';
        };
        reader.readAsText(file);
      });
    }

    // ── AI & Trainer CSV Guide Modal ───────────────────
    const csvGuide = $('[data-wt-csv-guide]');
    const csvGuideBtn = $('[data-wt-csv-guide-btn]');
    if (csvGuide && csvGuide.parentNode !== document.body) {
      document.body.appendChild(csvGuide);
    }
    function openCsvGuide() {
      if (!csvGuide) return;
      csvGuide.hidden = false;
      lockScroll();
      const panel = csvGuide.querySelector('.wt-csv-guide__panel');
      if (panel) trapFocus(panel);
    }
    function closeCsvGuide() {
      if (!csvGuide) return;
      csvGuide.hidden = true;
      unlockScroll();
    }
    if (csvGuideBtn) csvGuideBtn.addEventListener('click', openCsvGuide);
    if (csvGuide) {
      csvGuide.querySelectorAll('[data-wt-csv-guide-close]').forEach((b) => b.addEventListener('click', closeCsvGuide));
    }

    // 1-Click Copy AI Prompt
    const copyAiPromptBtn = document.querySelector('[data-wt-copy-ai-prompt]');
    if (copyAiPromptBtn) {
      copyAiPromptBtn.addEventListener('click', () => {
        const promptEl = document.querySelector('[data-wt-ai-prompt-text]');
        if (!promptEl) return;
        const text = promptEl.textContent || promptEl.innerText;
        navigator.clipboard.writeText(text).then(() => {
          const span = copyAiPromptBtn.querySelector('span');
          if (span) {
            const original = span.textContent;
            span.textContent = 'Copied to Clipboard!';
            copyAiPromptBtn.style.background = 'rgba(57,255,20,0.25)';
            setTimeout(() => {
              span.textContent = original;
              copyAiPromptBtn.style.background = '';
            }, 2000);
          }
          showToast('AI prompt copied! Paste into ChatGPT or Claude.');
        }).catch(() => {
          showToast('Failed to copy. Please select and copy text manually.');
        });
      });
    }

    // ── Body weight panel ────────────────────────────
    const bwDate  = $('#wt-bw-date');
    const bwValue = $('#wt-bw-value');
    const bwUnit  = $('[data-wt-bw-unit]');
    const bwList  = $('[data-wt-bw-list]');
    if (bwDate) bwDate.value = todayISO();
    function syncBwUnit() { bwUnit.textContent = state.session.unit; }
    syncBwUnit();
    unitInput.addEventListener('change', syncBwUnit);

    $('[data-wt-bw-add]').addEventListener('click', () => {
      const v = parseFloat(bwValue.value);
      if (!Number.isFinite(v) || v <= 0) return;
      WD.addBodyWeight({ date: bwDate.value || todayISO(), weight: v, unit: state.session.unit });
      bwValue.value = '';
      renderBodyWeight();
    });

    function renderBodyWeight() {
      const entries = WD.getBodyWeight().slice().reverse(); // newest first
      if (!entries.length) {
        bwList.innerHTML = `
          <div class="wt__empty">
            <p>No bodyweight entries yet.</p>
          </div>`;
        return;
      }
      bwList.innerHTML = entries.map((e) => `
        <div class="wt-bw-row" data-date="${escapeHTML(e.date)}">
          <span class="wt-bw-row__date">${formatDate(e.date)}</span>
          <span class="wt-bw-row__value">${escapeHTML(String(+e.weight))} <span style="font-size:.7rem;color:rgba(255,255,255,.55);font-weight:600;letter-spacing:.06em;">${escapeHTML(e.unit || 'kg')}</span></span>
          <button type="button" class="wt-bw-row__del" aria-label="Delete bodyweight entry for ${escapeHTML(formatDate(e.date))}">×</button>
        </div>
      `).join('');
      $$('.wt-bw-row__del', bwList).forEach((btn) => {
        btn.addEventListener('click', () => {
          const date = btn.closest('.wt-bw-row').dataset.date;
          WD.deleteBodyWeight(date);
          renderBodyWeight();
        });
      });
    }

    // Local calendar day key helper (avoids UTC timezone day shifting)
    function toLocalDayKey(d) {
      if (!d) return '';
      if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      const date = typeof d === 'string' ? new Date(d) : d;
      if (!date || isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }

    // ── Hero stats — show live numbers when there's data ─────────────
    const heroEl       = root.querySelector('.wt-hero');
    const heroStatsEl  = root.querySelector('[data-wt-hero-stats]');
    const heroBadgesEl = root.querySelector('[data-wt-hero-badges]');
    function renderHeroStats() {
      if (!heroEl || !heroStatsEl) return;
      const sessions = WD.getSessions();
      const bw       = WD.getBodyWeight();
      const hasData  = sessions.length > 0 || bw.length > 0;

      // Default markup is: stats VISIBLE (with "—" placeholders), badges HIDDEN.
      // For returning users (has data) we leave that as-is and just update
      // the values — no layout swap, no glitch on desktop.
      // For new users (no data) we hide stats and reveal the empty-state badges.
      heroStatsEl.hidden = !hasData;
      if (heroBadgesEl) heroBadgesEl.hidden = hasData;
      heroEl.dataset.hasData = hasData ? 'true' : 'false';

      // Stats strip — Sessions / Streak / Volume / PRs
      const sessionsEl = root.querySelector('[data-wt-hero-sessions]');
      if (sessionsEl) sessionsEl.textContent = sessions.length;

      const sessionDays = new Set(sessions.map((s) => toLocalDayKey(s.date)));
      let streak = 0;
      const today = new Date();
      const todayK = toLocalDayKey(today);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayK = toLocalDayKey(yesterday);

      // Morning streak resilience: if today hasn't been logged yet, but yesterday was,
      // count streak from yesterday so morning visits retain active streak.
      const startCheckDate = sessionDays.has(todayK) ? today : (sessionDays.has(yesterdayK) ? yesterday : null);
      if (startCheckDate) {
        const cur = new Date(startCheckDate);
        while (sessionDays.has(toLocalDayKey(cur))) {
          streak++;
          cur.setDate(cur.getDate() - 1);
        }
      }
      const streakEl = root.querySelector('[data-wt-hero-streak]');
      if (streakEl) streakEl.textContent = streak;

      const totalVol = sessions.reduce((sum, s) => sum + WD.sessionVolume(s), 0);
      const volEl = root.querySelector('[data-wt-hero-volume]');
      if (volEl) volEl.textContent = totalVol >= 1000 ? (totalVol / 1000).toFixed(1) + 'K' : totalVol;

      const prCount = WD.loggedExerciseNames().length;
      const prEl = root.querySelector('[data-wt-hero-prs]');
      if (prEl) prEl.textContent = prCount;

      // ── Right-side dashboard cards ────────────────────────────
      // 1. Streak card — same value + a 7-day dot row showing which of the
      //    last 7 days (oldest → newest, today on the right) had a session
      const streakCard = root.querySelector('[data-wt-hero-card="streak"]');
      const streakValueEl = root.querySelector('[data-wt-hero-card-streak-value]');
      const weekDots = root.querySelectorAll('[data-wt-hero-weekdots] .wt-hero__weekdot');
      if (streakValueEl) streakValueEl.textContent = streak;
      if (streakCard) streakCard.dataset.active = streak > 0 ? 'true' : 'false';
      weekDots.forEach((dot, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        const on = sessionDays.has(toLocalDayKey(d));
        dot.dataset.on = on ? 'true' : 'false';
        if (toLocalDayKey(d) === todayK) dot.style.outline = '1px solid rgba(255,255,255,0.10)';
      });

      // 2. Last PR card — find most recent PR across all exercises
      const prCard       = root.querySelector('[data-wt-hero-card="pr"]');
      const prNameEl     = root.querySelector('[data-wt-hero-card-pr-name]');
      const prDetailEl   = root.querySelector('[data-wt-hero-card-pr-detail]');
      let lastPr = null;
      sessions.forEach((s) => {
        s.exercises.forEach((ex) => {
          const isCardio = isCardioExercise(ex);
          ex.sets.forEach((set) => {
            if (!set.completed || set.type === 'warmup') return;
            if (isCardio) {
              const t = set.time != null ? set.time : (set.reps || 0);
              const d = set.distance != null ? set.distance : (set.weight || 0);
              if (t <= 0 && d <= 0) return;
              if (!lastPr || new Date(s.date) > new Date(lastPr.date)) {
                const du = set.distanceUnit || (s.unit === 'lb' ? 'mi' : 'km');
                lastPr = {
                  name: ex.name,
                  isCardio: true,
                  detail: `${t} min${d > 0 ? ' · ' + d + ' ' + du : ''}`,
                  date: s.date
                };
              }
            } else {
              if (!set.weight || !set.reps) return;
              const oneRm = WD.estimated1RM(set.weight, set.reps);
              if (!lastPr || new Date(s.date) > new Date(lastPr.date) ||
                  (toLocalDayKey(s.date) === toLocalDayKey(lastPr.date) && (!lastPr.isCardio && oneRm > lastPr.oneRm))) {
                lastPr = {
                  name: ex.name,
                  isCardio: false,
                  weight: set.weight, reps: set.reps, unit: set.unit || s.unit || 'kg',
                  date: s.date, oneRm,
                  detail: `${set.weight} ${set.unit || s.unit || 'kg'} × ${set.reps} · est. 1RM ${oneRm} ${set.unit || s.unit || 'kg'}`
                };
              }
            }
          });
        });
      });
      if (lastPr) {
        if (prCard)     prCard.dataset.active = 'true';
        if (prNameEl)   prNameEl.textContent  = lastPr.name;
        if (prDetailEl) prDetailEl.textContent = lastPr.detail;
      } else {
        if (prCard)     prCard.dataset.active = 'false';
        if (prNameEl)   prNameEl.textContent  = '—';
        if (prDetailEl) prDetailEl.textContent = 'Log your first set to set your benchmark.';
      }

      // 3. Weekly volume card — this week's total + last 7 days mini bars
      const volCard      = root.querySelector('[data-wt-hero-card="volume"]');
      const volCardValue = root.querySelector('[data-wt-hero-card-volume-value]');
      const volCardUnit  = root.querySelector('[data-wt-hero-card-volume-unit]');
      const minibars     = root.querySelectorAll('[data-wt-hero-minibars] .wt-hero__minibar');
      // Aggregate per-day volume for the last 7 days
      const dayBuckets = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
        dayBuckets.push({ key: toLocalDayKey(d), volume: 0 });
      }
      sessions.forEach((s) => {
        const k = toLocalDayKey(s.date);
        const b = dayBuckets.find((x) => x.key === k);
        if (b) b.volume += WD.sessionVolume(s);
      });
      const weekVol = dayBuckets.reduce((sum, b) => sum + b.volume, 0);
      const recentUnit = sessions[0]?.unit || 'kg';
      if (volCardValue) volCardValue.textContent = weekVol >= 1000 ? (weekVol / 1000).toFixed(1) + 'K' : weekVol.toLocaleString();
      if (volCardUnit)  volCardUnit.textContent  = recentUnit + '·vol';
      if (volCard)      volCard.dataset.active = weekVol > 0 ? 'true' : 'false';
      const maxVol = Math.max.apply(null, dayBuckets.map((b) => b.volume).concat([1]));
      minibars.forEach((bar, i) => {
        const v = dayBuckets[i].volume;
        const pct = (v / maxVol) * 100;
        bar.style.height = (v > 0 ? Math.max(6, pct * 0.18 + 4) : 4) + 'px';
        if (v > 0) bar.dataset.h = pct.toFixed(0); else delete bar.dataset.h;
      });
    }

    // ── Init ─────────────────────────────────────────
    // Restore in-progress draft so a refresh / accidental nav doesn't lose work
    const draft = loadDraft();
    if (draft && draft.exercises && draft.exercises.length) {
      state.session = Object.assign(state.session, draft);
      if (dateInput) dateInput.value = state.session.date;
      updateDatePill();
      setCustomSelect(unitInput, state.session.unit);
      // Restore session name from draft so the field isn't blank after refresh
      const nameInputRestore = $('[data-wt-session-name]');
      if (nameInputRestore && state.session.name) nameInputRestore.value = state.session.name;
    }

    // ── Session name ────────────────────────────────────────────────
    // The liquid renders a simple label + input (no collapsible toggle).
    // Wire the input so its value is kept in state (for draft persistence)
    // and read at save-time from the DOM.
    const sessionNameEl = $('[data-wt-session-name]');
    if (sessionNameEl) {
      sessionNameEl.addEventListener('input', () => {
        state.session.name = sessionNameEl.value.trim();
        persistDraft();
      });
    }

    // Legacy toggle refs — kept so older liquid variants still work if
    // they have the collapsible widget; no-ops if the elements don't exist.
    const labelToggleBtn = $('[data-wt-label-toggle]');
    const labelBodyEl2   = $('[data-wt-label-body]');
    const labelTextEl2   = $('[data-wt-label-text]');
    if (labelToggleBtn && labelBodyEl2) {
      labelToggleBtn.addEventListener('click', () => {
        const expanded = labelToggleBtn.getAttribute('aria-expanded') === 'true';
        labelToggleBtn.setAttribute('aria-expanded', !expanded ? 'true' : 'false');
        labelBodyEl2.hidden = expanded;
        if (!expanded && sessionNameEl) setTimeout(() => sessionNameEl.focus(), 60);
      });
      if (sessionNameEl && labelTextEl2) {
        sessionNameEl.addEventListener('input', () => {
          const v = sessionNameEl.value.trim();
          labelTextEl2.textContent = v || 'Name this session\u2026';
        });
      }
    }

    renderExercises();
    updateHistoryCount();
    renderHeroStats();

    // Refresh hero whenever data changes (save, delete, body-weight log)
    window.addEventListener('workout-data:change', renderHeroStats);
  }
})();
